import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Check, GlassWater, Ticket, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import PageSeo from "@/components/PageSeo";

const POSITIONS = ["Barkeeper & Service", "Kasse & Garderobe", "Türsteher / Security", "Egal / Sonstiges"];
const EXPERIENCES = ["Keine Erfahrung", "Etwas Erfahrung", "Mehrjährige Erfahrung"];
const AVAILABILITY = ["Freitag", "Samstag", "Events unter der Woche"];

const benefits = [
  { icon: GlassWater, title: "Barkeeper & Service", desc: "Drinks mixen, Gäste betreuen, Teamwork an der Bar" },
  { icon: Ticket, title: "Kasse & Garderobe", desc: "Empfang, Abrechnung und Organisation am Eingang" },
  { icon: ShieldCheck, title: "Türsteher / Security", desc: "Einlasskontrolle, Sicherheit und ein souveränes Auftreten" },

];

const inputClass =
  "w-full px-4 py-3 bg-card border border-border rounded text-foreground placeholder:text-muted-foreground font-[Montserrat] text-sm focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors";
const labelClass =
  "block font-[Montserrat] text-xs tracking-[0.15em] uppercase text-primary/80 mb-2";

const Jobs = () => {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    isAdult: "",
    experience: "",
    startDate: "",
    message: "",
    consent: false,
  });
  const [availability, setAvailability] = useState<string[]>([]);

  const set = (key: string, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleAvailability = (value: string) =>
    setAvailability((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (availability.length === 0) {
      toast({ title: "Verfügbarkeit fehlt", description: "Bitte wähle mindestens einen Tag aus.", variant: "destructive" });
      return;
    }
    if (file && file.size > 5 * 1024 * 1024) {
      toast({ title: "Datei zu groß", description: "Der Lebenslauf darf max. 5 MB groß sein.", variant: "destructive" });
      return;
    }

    setSending(true);
    try {
      const body = new FormData();
      body.append("name", form.name);
      body.append("email", form.email);
      body.append("phone", form.phone);
      body.append("position", form.position);
      body.append("experience", form.experience);
      body.append("startDate", form.startDate);
      body.append("message", form.message);
      body.append("isAdult", form.isAdult === "ja" ? "true" : "false");
      body.append("consent", form.consent ? "true" : "false");
      body.append("availability", JSON.stringify(availability));
      if (file) body.append("cv", file);

      const { data, error } = await supabase.functions.invoke("submit-job-application", { body });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setDone(true);
    } catch (err: any) {
      toast({
        title: "Fehler",
        description: err?.message || "Bewerbung konnte nicht gesendet werden. Bitte versuche es später erneut.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageSeo
        title="Jobs & Bewerbung – Luther Club Kaiserslautern"
        description="Jetzt online bewerben beim Luther Club Kaiserslautern: Barkeeper, Service, Kasse & Garderobe. Minijob oder Aushilfe am Wochenende."
        path="/jobs"
        jsonLd={benefits.slice(0, 2).map((b) => ({
          "@context": "https://schema.org",
          "@type": "JobPosting",
          title: b.title,
          description: b.desc,
          employmentType: "PART_TIME",
          datePosted: "2026-01-01",
          hiringOrganization: {
            "@type": "Organization",
            name: "Luther Club Kaiserslautern",
            sameAs: "https://lutherclub.de",
          },
          jobLocation: {
            "@type": "Place",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Martin-Luther-Straße 8",
              postalCode: "67657",
              addressLocality: "Kaiserslautern",
              addressCountry: "DE",
            },
          },
        }))}
      />


      <section className="pt-32 pb-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-semibold text-primary mb-4">
            Werde Teil des Teams
          </h1>
          <p className="text-foreground/80 text-lg leading-relaxed">
            Nachtleben, ein starkes Team und Wochenenden, die in Erinnerung bleiben:
            Wir suchen Verstärkung für den Luther Club in Kaiserslautern. Erfahrung ist
            ein Plus, aber kein Muss – Zuverlässigkeit und Lust auf Gastfreundschaft zählen.
          </p>
        </div>
      </section>

      <section className="px-4 pb-16">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-4">
          {benefits.map((b) => (
            <div key={b.title} className="p-5 rounded border border-border/50 bg-card/50">
              <b.icon className="w-5 h-5 text-primary mb-3" />
              <h2 className="font-semibold text-foreground mb-1">{b.title}</h2>
              <p className="text-sm text-muted-foreground">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 pb-24">
        <div className="max-w-2xl mx-auto">
          {done ? (
            <div className="text-center border border-primary/40 rounded-lg p-10 bg-card/60">
              <Check className="w-10 h-10 text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-primary mb-3">Bewerbung gesendet!</h2>
              <p className="text-foreground/80">
                Danke für dein Interesse. Wir haben dir eine Bestätigung per E-Mail geschickt
                und melden uns zeitnah bei dir.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <h2 className="text-2xl font-semibold text-primary text-center mb-2">
                Online bewerben
              </h2>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="name">Vor- und Nachname *</label>
                  <input id="name" required maxLength={120} className={inputClass}
                    value={form.name} onChange={(e) => set("name", e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="email">E-Mail *</label>
                  <input id="email" type="email" required maxLength={255} className={inputClass}
                    value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="phone">Telefon *</label>
                  <input id="phone" type="tel" required maxLength={40} className={inputClass}
                    value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="position">Position *</label>
                  <select id="position" required className={inputClass}
                    value={form.position} onChange={(e) => set("position", e.target.value)}>
                    <option value="">Bitte wählen</option>
                    {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="isAdult">Bist du 18 oder älter? *</label>
                  <select id="isAdult" required className={inputClass}
                    value={form.isAdult} onChange={(e) => set("isAdult", e.target.value)}>
                    <option value="">Bitte wählen</option>
                    <option value="ja">Ja</option>
                    <option value="nein">Nein</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="experience">Erfahrung in der Gastro *</label>
                  <select id="experience" required className={inputClass}
                    value={form.experience} onChange={(e) => set("experience", e.target.value)}>
                    <option value="">Bitte wählen</option>
                    {EXPERIENCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <span className={labelClass}>Verfügbarkeit *</span>
                <div className="flex flex-wrap gap-3">
                  {AVAILABILITY.map((day) => {
                    const active = availability.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleAvailability(day)}
                        className={`px-4 py-2 rounded border font-[Montserrat] text-xs tracking-[0.1em] uppercase transition-colors ${
                          active
                            ? "border-primary bg-primary/15 text-primary"
                            : "border-border text-foreground/70 hover:border-primary/40"
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className={labelClass} htmlFor="startDate">Frühester Starttermin</label>
                <input id="startDate" maxLength={60} placeholder="z. B. ab sofort" className={inputClass}
                  value={form.startDate} onChange={(e) => set("startDate", e.target.value)} />
              </div>

              <div>
                <label className={labelClass} htmlFor="message">Über dich / Nachricht</label>
                <textarea id="message" rows={5} maxLength={2000} className={inputClass}
                  value={form.message} onChange={(e) => set("message", e.target.value)} />
              </div>

              <div>
                <span className={labelClass}>Lebenslauf (PDF, JPG oder PNG, max. 5 MB)</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded border border-border hover:border-primary/40 font-[Montserrat] text-xs tracking-[0.1em] uppercase text-foreground/80 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  {file ? "Datei ändern" : "Datei auswählen"}
                </button>
                {file && (
                  <p className="mt-2 font-[Montserrat] text-xs text-muted-foreground break-all">
                    {file.name}
                  </p>
                )}
              </div>

              <label className="flex items-start gap-3 text-sm text-foreground/70">
                <input
                  type="checkbox"
                  required
                  checked={form.consent}
                  onChange={(e) => set("consent", e.target.checked)}
                  className="mt-1 accent-primary"
                />
                <span>
                  Ich bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner
                  Bewerbung gespeichert und verarbeitet werden. Mehr dazu in der{" "}
                  <Link to="/datenschutz" className="text-primary hover:underline">
                    Datenschutzerklärung
                  </Link>.
                </span>
              </label>

              <button
                type="submit"
                disabled={sending}
                className="w-full font-[Montserrat] text-sm tracking-[0.15em] uppercase px-8 py-4 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {sending ? "Wird gesendet…" : "Bewerbung absenden"}
              </button>

              <p className="text-center text-xs text-muted-foreground font-[Montserrat]">
                Lieber persönlich? Schreib uns per{" "}
                <a href="https://wa.me/491742022890" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">WhatsApp</a>{" "}
                oder an{" "}
                <a href="mailto:info@luther-kl.com" className="text-primary hover:underline">info@luther-kl.com</a>.
              </p>
            </form>
          )}
        </div>
      </section>
    </>
  );
};

export default Jobs;
